import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import InlineEdit from './InlineEdit';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Checkbox, message, Popconfirm, Popover } from 'antd';
import { nullSafeGet, nullSafeGetOrElse, stopDefaultEvents } from '../../utils/DataAccessUtils';
import moment from 'moment';
import WOTaskAdditionalDetails from '../work_order_tasks/WOTaskAdditionalDetails';
import { connect } from 'react-redux';
import WalkthroughTaskWithWorkOrderMessage from '../walkthroughs_detail_details_page/WalkthroughTaskWithWorkOrderMessage';
import InlineHyperlink from '../common/InlineHyperlink';
import { withUserType } from '../withUserType';
import { isTaskChecked } from '../../utils/TaskItemUtils';

const EachWOTask: FC<any> = ({
	task,
	onEnter,
	onSuccess,
	onDeleteSuccess,
	canEdit,
	onShowAssociate,
	workOrder,

	currentUser,
	onTaskEdit,
	onTaskDelete,
	timeZoneId,
	notCheckable,
	associationDisabled = false,
	hideIndividualWalkthroughLink,
	userType,
}): React.ReactElement => {
	const [editMode, setEditMode] = useState(false);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		setEditMode(!nullSafeGetOrElse('title', task, '').trim());
	}, [task]);

	const onFinish = useCallback(
		(title) => {
			if (!(title || '').trim()) return;

			const entity = {
				...task,
				questionType: 'checkbox',
				title,
				...(!task.createdAt && { createdAt: moment.utc() }),
			};
			setSaving(true);
			onTaskEdit(entity)
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onSuccess, onTaskEdit, task]
	);

	const isChecked = useMemo(() => isTaskChecked(task), [task]);

	const canCheck = useMemo(
		() => (isChecked ? task.answeredBy === currentUser.email : true),
		[currentUser.email, isChecked, task.answeredBy]
	);

	const walkthroughExists = useMemo(
		() => !hideIndividualWalkthroughLink && !!nullSafeGet('walkthrough', task),
		[hideIndividualWalkthroughLink, task]
	);

	const walkthrouNoteComplatedMessage = useMemo(
		() =>
			walkthroughExists
				? nullSafeGet('walkthrough.status', task) !== 'complete'
					? 'Walkthrough needs to be completed!'
					: ''
				: '',
		[task, walkthroughExists]
	);

	const onCheck = useCallback(() => {
		if (!nullSafeGetOrElse('title', task, '').trim() || !canCheck) {
			return;
		}

		if (!isChecked && walkthrouNoteComplatedMessage) {
			message.error(walkthrouNoteComplatedMessage);
			return;
		}

		const answer = nullSafeGetOrElse('answer', task, 'false') === 'false';
		setSaving(true);
		onTaskEdit({
			...task,
			answer: `${answer}`,
			...(!!answer && { answeredBy: currentUser.email }),
			...(!!answer && { answeredAt: moment.utc() }),
		})
			.then(onSuccess)
			.catch(message.error)
			.finally(() => setSaving(false));
	}, [
		canCheck,
		currentUser.email,
		isChecked,
		onSuccess,
		onTaskEdit,
		task,
		walkthrouNoteComplatedMessage,
	]);

	const onDeleteTask = useCallback(
		(e) => {
			if (saving) return;
			stopDefaultEvents(e);

			setSaving(true);
			onTaskDelete(task)
				.then(onDeleteSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[onDeleteSuccess, onTaskDelete, saving, task]
	);

	const onItemClick = useCallback(() => {
		canEdit && !saving && setEditMode(true);
	}, [canEdit, saving]);

	const _showAssociateModal = useCallback(() => {
		if (walkthroughExists) {
			message.error('Walkthrough already scheduled!');
			return;
		}

		onShowAssociate(task);
	}, [onShowAssociate, task, walkthroughExists]);

	const linkedWorkOrderId = useMemo(
		() => nullSafeGet('linkedWorkOrderTaskItem.workOrderId', task),
		[task]
	);

	const checkboxPopoverMessage = useMemo(() => {
		return !canCheck ? (
			<div>You are not allowed to uncheck this task</div>
		) : !!linkedWorkOrderId ? (
			<WalkthroughTaskWithWorkOrderMessage linkedWorkOrderId={linkedWorkOrderId} />
		) : null;
	}, [canCheck, linkedWorkOrderId]);

	const linkedWorkOrderLink = useMemo(
		() =>
			linkedWorkOrderId ? (
				<>
					<InlineHyperlink
						path={`/${userType}/workOrders/detail/${linkedWorkOrderId}/details`}
						text={`WO ${linkedWorkOrderId}`}
					/>{' '}
				</>
			) : null,
		[linkedWorkOrderId, userType]
	);

	const createdFromWalkthroughId = useMemo(
		() => nullSafeGet('createdFromWalkthroughTaskItem.walkthrough.id', task),
		[task]
	);

	const createdFromWalkthroughLink = useMemo(() => {
		return !hideIndividualWalkthroughLink && !!createdFromWalkthroughId ? (
			<>
				<InlineHyperlink
					path={`/${userType}/siteSurveys/walkthrough/detail/${createdFromWalkthroughId}/details`}
					text={nullSafeGetOrElse(
						'createdFromWalkthroughTaskItem.walkthrough.title',
						task,
						`Walkthrough #${createdFromWalkthroughId}`
					)}
				/>{' '}
			</>
		) : null;
	}, [createdFromWalkthroughId, hideIndividualWalkthroughLink, task, userType]);

	const deleteConfirmProps = useMemo(() => {
		if (!!createdFromWalkthroughId) {
			return {
				title: `This task will be removed from this work order and walkthrough '${nullSafeGetOrElse(
					'createdFromWalkthroughTaskItem.walkthrough.title',
					task,
					''
				)}'`,
				okText: 'Delete from both',
				cancelText: 'Cancel',
			};
		} else {
			return {
				title: 'Are you sure you want to delete?',
				okText: 'Yes',
				cancelText: 'No',
			};
		}
	}, [createdFromWalkthroughId, task]);

	return (
		<div className="group mb-2 flex flex-row items-center justify-between">
			<div className="mr-2 flex w-full flex-col items-start justify-start">
				<div
					className={`mr-2 flex flex-row ${notCheckable ? 'items-baseline' : 'items-start'} ${
						isChecked ? '' : 'w-full'
					}`}
				>
					{notCheckable ? (
						<div>-</div>
					) : (
						<Popover trigger="hover" content={checkboxPopoverMessage}>
							<div className="pt-1">
								<Checkbox
									onClick={onCheck}
									checked={isChecked}
									disabled={!!checkboxPopoverMessage}
								/>
							</div>
						</Popover>
					)}
					{editMode && canEdit && !isChecked ? (
						<InlineEdit
							placeholder="Enter task title"
							value={task.title}
							setValue={onFinish}
							onEnter={onEnter}
						/>
					) : (
						<div
							className={`gap-2 pl-2 pt-1 ${canEdit ? 'hover:cursor-pointer' : ''} ${
								isChecked ? 'line-through' : ''
							}`}
							onClick={onItemClick}
						>
							{createdFromWalkthroughLink}
							{linkedWorkOrderLink}
							{nullSafeGetOrElse('title', task, '')}
						</div>
					)}
					{!linkedWorkOrderId ? (
						<div
							className={`ml-4 flex hidden flex-row items-center gap-4 pt-1 ${
								canEdit ? 'hover:cursor-pointer group-hover:flex' : ''
							}`}
						>
							{!editMode ? (
								<>
									{!isChecked ? (
										<div>
											<EditOutlined onClick={onItemClick} />
										</div>
									) : null}
									{!associationDisabled ? (
										<div>
											<PlusOutlined onClick={_showAssociateModal} />
										</div>
									) : null}
								</>
							) : null}
							<div>
								{task.id ? (
									<Popconfirm
										onConfirm={onDeleteTask}
										placement="topLeft"
										title={deleteConfirmProps.title}
										okText={deleteConfirmProps.okText}
										cancelText={deleteConfirmProps.cancelText}
									>
										<DeleteOutlined />
									</Popconfirm>
								) : (
									<DeleteOutlined onClick={onDeleteTask} />
								)}
							</div>
						</div>
					) : null}
				</div>
				<div className="ml-4">
					<WOTaskAdditionalDetails
						task={task}
						timeZone={timeZoneId}
						onSave={onTaskEdit}
						workOrder={workOrder}
						hideIndividualWalkthroughLink={hideIndividualWalkthroughLink}
					/>
				</div>
			</div>
			<div className={`h-4 w-4 ${canEdit ? 'group-hover:hidden' : ''}`} />
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default withUserType(connect(mapStateToProps)(EachWOTask as any) as any);
